// src/components/CalculatorLoader.js
import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';

const TALL_CALCULATORS = [
  'ai-usage-score-calculator',
  'fitness-calculator',
  'longevity-lifestyle-calculator',
  'study-efficiency-calculator',
  'ai-tutor-cost-comparison'
];

const CalculatorLoader = ({ calculatorSlug }) => {
  const [calculator, setCalculator] = useState(null);
  const [calculatorFile, setCalculatorFile] = useState(null);
  const [fileExists, setFileExists] = useState(true);
  const [loading, setLoading] = useState(true);
  const { darkMode } = useDarkMode();
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    const loadCalculator = async () => {
      try {
        setLoading(true);

        // 1. Fetch calculator metadata from API
        const response = await apiService.getCalculator(calculatorSlug);
        if (!response.success || !response.data) {
          throw new Error('Calculator not found in API');
        }
        setCalculator(response.data);

        // 2. Once API succeeds, check registry
        //const registryResponse = await fetch('/calc-files/registry.json');
        //const registry = await registryResponse.json();
        //const file = registry[calculatorSlug];
        const file = calculatorSlug + '.html';
        if (!file) {
          throw new Error('Calculator not in registry');
        }

        // 3. Check if HTML file exists
        /*const fileCheck = await fetch(`/calc-files/${file}`);
        if (!fileCheck.ok) {
          throw new Error('Calculator file not found on server');
        }
         */ 
        setCalculatorFile(file);
        setFileExists(true);
      } catch (error) {
        console.error('Failed to load calculator:', error);
        setFileExists(false);
      } finally {
        setLoading(false);
      }
    };

    loadCalculator();
  }, [calculatorSlug]);

  // Force iframe reload when dark mode changes
  useEffect(() => {
    setIframeKey(prev => prev + 1);
  }, [darkMode]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
        <p className="mt-2 text-slate-600 dark:text-slate-300">Loading calculator...</p>
      </div>
    );
  }

  if (!fileExists || !calculator) {
    return (
      <div className="text-center py-12 calculator-card rounded-lg border border-red-200 mx-4">
        <div className="text-amber-500 text-6xl mb-4">🚧</div>
        <h3 className="text-xl font-bold text-amber-500 mb-2">Calculator coming soon...</h3>
        <p className="text-amber-600">We're building something amazing for you!</p>
        <div className="mt-8">
            <AdBanner adKey="857d3637cfc0b322e60f1c5c2a864a62" width={320} height={50} />   
        </div>  
      </div>
    );
  }

  //const keywordsString = calculator.keywords?.join(', ') || ''; //This line is for DB fetch
  const keywordsArray = calculator.keywords?.split(",").map(keyword => keyword.trim()) || '';
  //const keywordsString =  article?.keywords ? article.keywords.join(', ') : ''; 
  const keywordsString =  keywordsArray ? keywordsArray.join(', ') : '';

  const iframeUrl = `/calc-files/${calculatorFile}?dark=${darkMode}`;
  const heightClass = TALL_CALCULATORS.includes(calculatorSlug) 
    ? "min-h-[2100px]" 
    : "min-h-[1270px]";

  return (
    <div className="w-full container rounded-lg overflow-hidden">
      <SEO
        title={calculator.name}
        description={calculator.meta_description}
        keywords={keywordsString}
        toolType="calculator"
      />
      <iframe
        key={iframeKey}
        src={iframeUrl}
        className={`w-full ${heightClass} border-0`}
        title={`${calculatorSlug} Calculator`}
        loading="lazy"
        onError={() => setFileExists(false)}
      />
    </div>
  );
};

export default CalculatorLoader;